<template>

<Column full class="mainmenu" padding="30px 40px">

  <!-- header -->
  <div class="row ch head">
    <h1>Stima</h1>
    <p class="desc" style="margin-left: 12px;">{{ today() }}</p>
    <p class="tag small pointer click" style="margin-left: auto;" :class="{off: loading}" @click="refresh()">{{ loading ? 'Loading...' : 'Refresh' }}</p>
    <p class="micro" style="margin-left: 10px;" v-if="updated">updated {{ tools.timeonly(updated) }}</p>
  </div>

  <p class="bad" v-if="error">{{ error }}</p>
  <p class="bad micro" v-for="(e, i) in s.errors || []" :key="i">{{ e }}</p>

  <div class="grid" v-if="loaded">

    <!-- growth: total + periods -->
    <div class="card" v-for="m in growth" :key="m.title">
      <p class="mini">{{ m.title }}</p>
      <p class="big">{{ m.format(m.data.total) }}</p>
      <div class="row periods">
        <div class="column left" v-for="p in periods" :key="p.key">
          <p class="micro">{{ p.title }}</p>
          <p class="num" :class="sign(m.data[p.key])">{{ signed(m.data[p.key], m.format) }}</p>
        </div>
      </div>
    </div>

    <!-- active users -->
    <div class="card">
      <p class="mini">Active users</p>
      <p class="big">{{ num(s.active.day) }} <span class="micro">24h</span></p>
      <div class="row periods">
        <div class="column left"><p class="micro">7 days</p><p class="num">{{ num(s.active.week) }}</p></div>
        <div class="column left"><p class="micro">30 days</p><p class="num">{{ num(s.active.month) }}</p></div>
        <div class="column left"><p class="micro">of all users</p><p class="num">{{ share(s.active.month, s.users.total) }}</p></div>
      </div>
    </div>

    <!-- value by category -->
    <div class="card wide">
      <div class="row ch">
        <p class="mini">Assets value by category</p>
        <div class="row ch tabs-mini">
          <p class="tag small pointer" :class="{active: rootsBy == 'sum'}" @click="rootsBy = 'sum'">Value</p>
          <p class="tag small pointer" :class="{active: rootsBy == 'count'}" @click="rootsBy = 'count'">Count</p>
        </div>
      </div>
      <Donut :items="rootItems" :format="rootsBy == 'sum' ? usd : num" :totalLabel="rootsBy == 'sum' ? 'total value' : 'assets'" label="Assets by category" :size="210"/>
    </div>

    <!-- support -->
    <div class="card">
      <div class="row ch">
        <p class="mini">Support</p>
        <router-link class="micro" style="margin-left: auto;" :to="{name: 'support'}">open →</router-link>
      </div>
      <div class="row periods" style="margin: 6px 0 10px;">
        <div class="column left"><p class="micro">waiting reply</p><p class="num" :class="{warn: s.chats.waiting}">{{ num(s.chats.waiting) }}</p></div>
        <div class="column left"><p class="micro">users today</p><p class="num">{{ num(s.chats.userToday) }}</p></div>
        <div class="column left"><p class="micro">replies today</p><p class="num">{{ num(s.chats.teamToday) }}</p></div>
        <div class="column left"><p class="micro">users 7d</p><p class="num">{{ num(s.chats.userWeek) }}</p></div>
      </div>
      <router-link class="row ch chat" v-for="c in support" :key="c.id" :to="{name: 'support/chat', params: {id: c.id}}">
        <avatar :image="c.image" :name="c.title" style="margin-right: 10px;"/>
        <div class="column left" style="min-width: 0; flex: 1;">
          <p class="cname">{{ c.title }} <span class="micro" style="float: right;">{{ tools.ago(c.updated) }}</span></p>
          <p class="cmsg"><span v-if="c.messageUID && c.messageUID != c.uid" class="micro">You: </span>{{ c.messageText || '...' }}</p>
        </div>
      </router-link>
      <p class="micro" v-if="!support.length">No support chats</p>
    </div>

    <!-- new users -->
    <div class="card">
      <div class="row ch">
        <p class="mini">New users</p>
        <router-link class="micro" style="margin-left: auto;" :to="{name: 'accounts'}">all →</router-link>
      </div>
      <router-link class="row ch chat" v-for="u in newUsers" :key="u.id" :to="{name: 'accounts/page', params: {id: u.id}}">
        <!-- флаг страны регистрации (geoip), страна и город по ховеру -->
        <img v-if="u.country" class="flag" :src="`/images/icons/flags/round/${u.country}.svg`" :title="place(u)" :alt="u.country">
        <div v-else class="flag noflag" title="Unknown location">?</div>
        <div class="column left" style="min-width: 0; flex: 1;">
          <p class="cname">{{ u.name || u.email || `#${u.id}` }} <span class="micro" style="float: right;">{{ tools.ago(u.created) }}</span></p>
          <p class="cmsg">{{ [u.email, u.country ? place(u) : ''].filter(Boolean).join(' · ') || '—' }}</p>
        </div>
      </router-link>
      <p class="micro" v-if="!newUsers.length">No users</p>
    </div>

    <!-- new assets -->
    <div class="card">
      <div class="row ch">
        <p class="mini">New assets</p>
        <router-link class="micro" style="margin-left: auto;" :to="{name: 'assets'}">all →</router-link>
      </div>
      <router-link class="row ch chat" v-for="a in newAssets" :key="a.id" :to="{name: 'assets/page', params: {id: a.id}}">
        <!-- global .preview is a fixed side panel, so the image box is .thumb -->
        <div class="thumb" :style="{backgroundImage: image(a) ? `url(${image(a)})` : ''}">
          <p class="micro" v-if="!image(a)">—</p>
        </div>
        <div class="column left" style="min-width: 0; flex: 1;">
          <p class="cname">{{ a.brandname || '—' }} <span class="micro" style="float: right;">{{ tools.ago(a.created) }}</span></p>
          <p class="cmsg">{{ a.title || a.pagetitle || '...' }}</p>
          <p class="cmsg">
            <span v-if="a.price">{{ usd(a.price) }} · </span>
            <span :style="{color: dict.statusColor(a.status || 0)}">{{ dict.statusName(a.status || 0) }}</span>
          </p>
        </div>
      </router-link>
      <p class="micro" v-if="!newAssets.length">No assets</p>
    </div>

    <!-- pipeline -->
    <div class="card">
      <p class="mini">Assets by status</p>
      <div class="column left bars">
        <div class="row ch bar" v-for="b in statusBars" :key="b.title" :title="`${b.title}: ${num(b.count)}`">
          <p class="blabel">{{ b.title }}</p>
          <div class="btrack"><div class="bfill dash-bfill" :style="{width: b.width}"></div></div>
          <p class="bval">{{ num(b.count) }}</p>
        </div>
      </div>
      <div class="row wrap flags">
        <p class="micro">🔒 private {{ num(s.flags.private) }}</p>
        <p class="micro">🚩 red flag {{ num(s.flags.redflag) }}</p>
        <p class="micro">locked {{ num(s.flags.locked) }}</p>
        <p class="micro">deleted {{ num(s.flags.deleted) }}</p>
      </div>
    </div>

    <!-- catalog -->
    <div class="card">
      <p class="mini">Catalog</p>
      <div class="row periods">
        <div class="column left"><p class="micro">categories</p><p class="num">{{ num(s.dict.roots) }}</p></div>
        <div class="column left"><p class="micro">brands</p><p class="num">{{ num(s.dict.brands) }}</p></div>
        <div class="column left"><p class="micro">models</p><p class="num">{{ num(s.dict.models) }}</p></div>
        <div class="column left"><p class="micro">submodels</p><p class="num">{{ num(s.dict.submodels) }}</p></div>
      </div>
    </div>

    <!-- files -->
    <div class="card">
      <p class="mini">Files</p>
      <p class="big">{{ num(s.files.count) }} <span class="micro">{{ bytes(s.files.bytes) }}</span></p>
      <div class="row periods">
        <div class="column left"><p class="micro">today</p><p class="num" :class="sign(s.files.today)">{{ signed(s.files.today, num) }} <span class="micro">{{ bytes(s.files.todayBytes) }}</span></p></div>
        <div class="column left" v-for="f in fileStatus" :key="f.key"><p class="micro">{{ f.key || 'no status' }}</p><p class="num">{{ num(f.count) }}</p></div>
      </div>
    </div>

    <!-- gpt -->
    <div class="card">
      <p class="mini">GPT cost</p>
      <p class="micro bad" v-if="s.gpt.error">{{ s.gpt.error }}</p>
      <div class="row periods" v-else>
        <div class="column left"><p class="micro">today</p><p class="num">{{ money(s.gpt.today) }}</p></div>
        <div class="column left"><p class="micro">30 days</p><p class="num">{{ money(s.gpt.month) }}</p></div>
        <div class="column left"><p class="micro">per active user 30d</p><p class="num">{{ s.active.month ? money(s.gpt.month / s.active.month) : '—' }}</p></div>
      </div>
    </div>

  </div>

</Column>

</template>

<script setup>
import avatar from '@/ui/avatar.vue';
import Donut from './dashboard/Donut.vue';
</script>
<script>
import { defineComponent } from 'vue';

// pipeline groups over pkg/dict/assets/status ids
const STATUS_GROUPS = [
  {title: 'Init / New', from: 0,   to: 10},
  {title: 'Detecting',  from: 10,  to: 20},
  {title: 'Filled',     from: 20,  to: 30},
  {title: 'Hold',       from: 30,  to: 40},
  {title: 'Ownership',  from: 40,  to: 200},
  {title: 'Verified',   from: 200, to: 300},
  {title: 'Minted',     from: 300, to: 400},
  {title: 'Stored',     from: 400, to: 500},
  {title: 'Tokenized',  from: 500, to: 600},
  {title: 'Sold',       from: 600, to: Infinity},
]

export default defineComponent({
  mounted() {
    this.refresh()
  },
  data() {
    return {
      s: {},
      support: [],
      newUsers: [],
      newAssets: [],
      loaded: false,
      loading: false,
      error: undefined,
      updated: undefined,
      rootsBy: 'sum',
      periods: [
        {key: 'today', title: 'today'},
        {key: 'week',  title: '7 days'},
        {key: 'month', title: '30 days'},
        {key: 'year',  title: '1 year'},
      ],
    }
  },
  computed: {
    growth(){
      return [
        {title: 'Users',             data: this.s.users,  format: this.num},
        {title: 'Assets',            data: this.s.assets, format: this.num},
        {title: 'Assets value, USD', data: this.s.value,  format: this.usd},
      ]
    },
    rootItems(){
      return (this.s.roots || []).map(p => ({
        key: p.id,
        title: p.title || (p.id ? `#${p.id}` : 'No category'),
        value: this.rootsBy == 'sum' ? p.sum : p.count,
      }))
    },
    statusBars(){
      let list = this.s.status || []
      let res = STATUS_GROUPS.map(g => ({
        title: g.title,
        count: list.filter(p => p.status >= g.from && p.status < g.to).reduce((s, p) => s + p.count, 0),
      }))
      let max = Math.max(1, ...res.map(p => p.count))
      return res.map(p => ({...p, width: `${p.count / max * 100}%`}))
    },
    fileStatus(){
      let m = this.s.files && this.s.files.status || {}
      return Object.keys(m).map(key => ({key, count: m[key]})).sort((a, b) => b.count - a.count).slice(0, 3)
    },
  },
  methods: {

    async refresh(){
      if (this.loading) return
      this.loading = true

      // "today" is the admin's local midnight
      let midnight = new Date()
      midnight.setHours(0, 0, 0, 0)

      let [res, chats, assets] = await Promise.all([
        this.api.send('admin/stats/dashboard', {today: Math.floor(midnight.getTime() / 1000)}),
        this.api.send('admin/chats/list/support', {limit: 5, offset: 0}),
        this.api.assets.search({sort: 'id', desc: true, limit: 5, eq: {deleted: false}}),
      ])
      this.loading = false

      if (res.error) {
        this.error = `Error: ${res.error.code}: ${res.error.id}`
        return
      }
      this.error = undefined
      this.s = res.resp || {}
      this.support = chats.error ? [] : (chats.resp || [])
      this.newUsers = this.s.newUsers || []
      this.newAssets = assets.error ? [] : (assets.resp || [])
      this.loaded = true
      this.updated = Math.floor(Date.now() / 1000)
    },

    num(v){ return Number(v || 0).toLocaleString('en-US') },

    // compact usd: $950, $12.4K, $3.1M, $1.2B
    usd(v){
      v = Number(v || 0)
      let a = Math.abs(v)
      if (a >= 1e9) return `$${(v / 1e9).toFixed(1)}B`
      if (a >= 1e6) return `$${(v / 1e6).toFixed(1)}M`
      if (a >= 1e4) return `$${(v / 1e3).toFixed(1)}K`
      return `$${v.toLocaleString('en-US')}`
    },

    // "Germany, Berlin" из iso2 кода страны
    place(u){
      let country = u.country
      try {
        country = new Intl.DisplayNames(['en'], {type: 'region'}).of(u.country.toUpperCase()) || u.country
      } catch (e) {
        country = u.country.toUpperCase()
      }
      return [country, u.city].filter(Boolean).join(', ')
    },

    image(item){
      return item.images && item.images.length ? item.images[0] : undefined
    },

    // +12 / −12 / 0: знак для приростов, цвет через sign()
    signed(v, format){
      v = Number(v || 0)
      if (v > 0) return '+' + format(v)
      if (v < 0) return '−' + format(-v)
      return '0'
    },

    sign(v){
      v = Number(v || 0)
      return v > 0 ? 'dash-pos' : v < 0 ? 'dash-neg' : 'zero'
    },

    money(v){ return `$${Number(v || 0).toFixed(2)}` },

    bytes(v){
      v = Number(v || 0)
      if (v >= 1024 ** 3) return `${(v / 1024 ** 3).toFixed(2)} GB`
      if (v >= 1024 ** 2) return `${(v / 1024 ** 2).toFixed(1)} MB`
      if (v >= 1024) return `${(v / 1024).toFixed(0)} KB`
      return `${v} B`
    },

    share(part, total){
      if (!total) return '—'
      let p = part / total * 100
      return `${p >= 10 ? Math.round(p) : p.toFixed(1)}%`
    },

    today(){
      return new Date().toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'})
    },
  },
});
</script>
<style scoped>
.head{
  width: 100%;
  margin-bottom: 20px;
}
.grid{
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
  width: 100%;
  text-align: left;
}
.card{
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 0;
  padding: 18px 20px;
  border-radius: 16px;
  background-color: var(--g05);
}
.card.wide{
  grid-column: span 2;
}
@media (max-width: 760px){
  .card.wide{ grid-column: span 1; }
}
.big{
  font-size: 28px;
  font-weight: 600;
  line-height: 1.2;
  margin: 4px 0 10px;
  font-variant-numeric: tabular-nums;
}
.periods{
  flex-wrap: wrap;
  gap: 18px;
}
.num{
  font-size: 15px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}
.num.zero{ color: var(--g4); }
.num.warn{ color: var(--bad); }

.tabs-mini{
  margin-left: auto;
  gap: 4px;
}

.chat{
  width: 100%;
  padding: 6px;
  border-radius: 10px;
  color: var(--text);
}
.chat:hover{ background-color: var(--g05); }
.cname{ font-size: 13px; font-weight: 500; }
.cmsg{
  font-size: 12px;
  color: var(--g5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* single series: one hue, value labels on every bar */
.flag{
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  margin-right: 10px;
  border-radius: 50%;
}
.noflag{
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: var(--g4);
  background-color: var(--g1);
}

.thumb{
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  margin-right: 10px;
  border-radius: 10px;
  background-color: var(--g1);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--g3);
}

.bars{ width: 100%; margin-top: 8px; gap: 5px; }
.bar{ width: 100%; }
.blabel{ font-size: 12px; width: 90px; flex-shrink: 0; color: var(--g6); }
.btrack{ flex: 1; height: 10px; }
.bfill{
  height: 100%;
  min-width: 2px;
  border-radius: 0 4px 4px 0;
  background-color: #2a78d6;
}
.bval{ font-size: 12px; width: 60px; text-align: right; font-variant-numeric: tabular-nums; }
.flags{ gap: 12px; margin-top: 12px; }
.off{ opacity: .5; pointer-events: none; }
</style>
<style>
/* не scoped: :global(body.dark) .x в scoped Vue компилирует в голый body.dark */
body.dark .dash-bfill{ background-color: #3987e5; }

/* приросты: зелёный плюс, светло-красный минус */
.dash-pos{ color: #1f9d55; }
.dash-neg{ color: #e5484d; }
body.dark .dash-pos{ color: #4cd07d; }
body.dark .dash-neg{ color: #ff8a8a; }
</style>
